import { statuses } from "@icg360/ipc-auth-js";

import { handleIXAuth } from "../promises/handle-ixauth";
import handlePXServerAuth from "../promises/handle-pxserver-auth";
import { requestResetPassword } from "../promises/request-reset-password";
import { requestUsername } from "../promises/request-username";
import { MESSAGE_TYPES } from "../components/messages/constants";
import store from "../store/login-store";
import { addMessage } from "../store/messages";
import { renderLoginForm } from "../login-entry";

import storage from "./storage";
import { write as writeCookie } from "./cookies";
import { isEmail, isPresent } from "./validation";

export function changeLoginFormView(view) {
  window.location.hash = `#${view}`;
  renderLoginForm({
    view,
    message: ""
  });
}

export async function submitLogin(username, password) {
  if (!username || !password) {
    renderLoginForm({
      hasError: true,
      message: "Please enter a value in both the Username and Password fields.",
      messageType: "error"
    });
    return;
  }

  renderLoginForm({
    message: "",
    isLoading: true
  });

  const b64Val = window.btoa(`${username}:${password}`);

  handleIXAuth({
    username,
    password,
    meta: { type: "login", username }
  })
    .then(ixAuthResponse => {
      if (ixAuthResponse.status !== statuses.success)
        return Promise.reject(ixAuthResponse, CONFIG);

      return handlePXServerAuth(username, b64Val, ENV_INFO.programID, CONFIG);
    })
    // Check pxServer response here for roles/rights, and load site on success
    .then(pxServerUser => {
      if (!checkPermissions(CONFIG, pxServerUser)) {
        return Promise.reject({ message: "Missing permissions." });
      }

      return loadFullSite(b64Val);
    })
    .catch(e => {
      // eslint-disable-next-line no-console
      console.warn(e.message ? e.message : e);
      const message = "Invalid username or password. Please try again.";

      renderLoginForm({
        isLoading: false,
        message,
        messageType: "error"
      });
    });
}

export function checkPermissions(config, pxServerXml) {
  // role list from config, options will vary between SageSure, FNIC, etc
  const roleList = config.envConfig.ipcm.values.loginWhitelist || [
    "CRU_AGENT_NY_HO",
    "PX_MANAGER"
  ];

  const hasRole = roleList.some(
    role => pxServerXml.querySelectorAll(`roles role[id="${role}"]`).length > 0
  );

  const hasRight =
    pxServerXml.querySelectorAll(
      `rights right[softwareapplication="agentportal"][organization="${ENV_INFO.programID}"][action="login"][access="grant"]`
    ).length > 0;

  // testing both in case there is an issue with the right being attached in the xml
  // or another role supporting login rights.  As long as one exists, we are good,
  // but if both are missing, the user does not have rights to login.
  return hasRole || hasRight;
}

export function sanitizeHash(originalHash) {
  const prohibitedHashes = ["forgot-username", "forgot-password"];
  return prohibitedHashes.indexOf(originalHash) === -1 ? originalHash : "";
}

export function loadFullSite(b64Val, agentToken) {
  // handle authenticated cookie here
  const date = new Date();
  date.setTime(date.getTime() + 24 * 60 * 60 * 1000 * 7); // expire after one week
  const cookieOptions = {
    expires: date
  };
  writeCookie("apb64", b64Val, cookieOptions);
  if (agentToken) {
    writeCookie("apagenttoken", agentToken, cookieOptions);
    writeCookie("Authorization", `AgentToken ${agentToken}`, cookieOptions);
  } else {
    writeCookie("Authorization", `Basic ${b64Val}`, cookieOptions);
  }
  storage.setItem("last-active", new Date().getTime());
  let redirectTo = `https://${window.location.host}/`;
  if (window.location.search) {
    const searchParams = new URLSearchParams(window.location.search);
    const redirectPath = searchParams.get("redirect");
    if (redirectPath) {
      redirectTo += `${
        redirectPath.startsWith("/") ? redirectPath.substring(1) : redirectPath
      }`;
      searchParams.delete("redirect");
    }
    if (searchParams.size > 0) {
      redirectTo += `?${searchParams.toString()}`;
    }
  } else if (window.location.hash) {
    redirectTo += `${sanitizeHash(window.location.hash)}`;
  }
  window.location.href = redirectTo;
}

export function submitForgotPassword(username) {
  if (!isPresent(username)) {
    renderLoginForm({
      message: "Please enter a value in the Username field.",
      messageType: "error"
    });
    return;
  }

  renderLoginForm({
    message: "",
    isLoading: true
  });

  requestResetPassword(username)
    .then(() => {
      renderLoginForm({
        message: "A password reset link has been sent to your email.",
        messageType: "",
        isLoading: false
      });
    })
    .catch(() => {
      renderLoginForm({
        message: "We could not process the reset request at this time.",
        messageType: "error",
        isLoading: false
      });
    });
}

export function submitForgotUsername(email) {
  if (!isPresent(email)) {
    renderLoginForm({
      message: "Please enter a value in the Email field.",
      messageType: "error"
    });
    return;
  }

  if (!isEmail(email)) {
    renderLoginForm({
      message: "Please enter a valid email in the field.",
      messageType: "error"
    });
    return;
  }

  renderLoginForm({
    message: "",
    isLoading: true
  });

  requestUsername(email)
    .then(() => {
      renderLoginForm({
        message: "A message was sent to the email you provided above.",
        messageType: "",
        isLoading: false
      });
    })
    .catch(() => {
      renderLoginForm({
        message: "We could not process the request at this time.",
        messageType: "error",
        isLoading: false
      });
    });
}

export function handleSAMLPassthroughAuth(authURI, redirect = "") {
  const agentToken = authURI.split("/").pop();
  handleIXAuth({
    agentToken,
    config: CONFIG.appConfig,
    meta: { type: "agentTokenLogin" }
  }).then(response => {
    const username = response.id;
    const b64Val = window.btoa(`${username}:`);
    if (response.status === statuses.success) {
      writeCookie("auth-session-uri", authURI);
      return loadFullSite(b64Val, agentToken);
    }
    renderLoginForm({
      message: "The SAML Passthrough attempt failed.",
      messageType: "error"
    });
  });
}

export function maintenanceMessage({
  message = "",
  start,
  end,
  lockout = false
}) {
  if (!message) return;

  const now = new Date().getTime();
  const startTime = start ? new Date(start).getTime() : now;
  const endTime = end ? new Date(end).getTime() : now;

  if (lockout && startTime < now && endTime >= now) {
    renderLoginForm({
      enabled: false,
      message,
      messageType: ""
    });
    return;
  }

  if (endTime >= now) {
    store.dispatch(
      addMessage({
        type: MESSAGE_TYPES.WARNING,
        text: message
      })
    );
  }
}
